import React from 'react';
import {
  Box,
  Typography,
  Alert,
  Chip,
  FormControlLabel,
  Switch
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import ScoringFactorCard, { NormalizedScoringFactorConfig } from './ScoringFactorCard';
import { ScoringWeightConfig } from '../../models/tenant';

/**
 * Props for the ScoringTab component
 */
export interface ScoringTabProps {
  scoringWeights: Record<string, ScoringWeightConfig>;
  onWeightsChange: (weights: Record<string, ScoringWeightConfig>) => void;
}

/**
 * Utility function to normalize scoring weights when a factor is changed
 * @param currentWeights - Current scoring weights
 * @param changedComponent - Component that was changed
 * @param newValue - New percentage value (0-100)
 * @param normalizeMode - Whether to normalize other factors
 * @returns Updated weights object
 */
export const normalizeScoringWeights = (
  currentWeights: Record<string, ScoringWeightConfig>,
  changedComponent: string,
  newValue: number,
  normalizeMode: boolean
): Record<string, ScoringWeightConfig> => {
  const newValueDecimal = newValue / 100;
  const newWeights = { ...currentWeights };

  // Set the changed slider to its exact value
  newWeights[changedComponent] = {
    ...currentWeights[changedComponent],
    value: newValueDecimal
  };

  // Only normalize other sliders if normalizeMode is enabled
  if (normalizeMode) {
    const remainingPercentage = 100 - newValue;

    // Get all other enabled factors (excluding the one being changed)
    const otherFactors = Object.entries(currentWeights)
      .filter(([key, config]) => key !== changedComponent && config.value > 0);

    const otherFactorsSum = otherFactors.reduce((acc, [_, config]) => acc + config.value, 0);

    // Normalize other sliders proportionally to fill the remaining percentage
    if (otherFactorsSum > 0 && remainingPercentage > 0) {
      otherFactors.forEach(([key, config]) => {
        const proportion = config.value / otherFactorsSum;
        newWeights[key] = {
          ...config,
          value: (remainingPercentage / 100) * proportion
        };
      });
    } else if (otherFactorsSum > 0 && remainingPercentage <= 0) {
      // If changed slider is at 100%, set all others to 0
      otherFactors.forEach(([key, config]) => {
        newWeights[key] = {
          ...config,
          value: 0
        };
      });
    }
  }

  return newWeights;
};

/**
 * Utility function to handle toggling scoring factors on/off
 * @param currentWeights - Current scoring weights
 * @param component - Component to toggle
 * @param enabled - Whether to enable or disable the factor
 * @param normalizeMode - Whether to normalize other factors when toggling
 * @returns Updated weights object
 */
export const toggleScoringFactor = (
  currentWeights: Record<string, ScoringWeightConfig>,
  component: string,
  enabled: boolean,
  normalizeMode: boolean
): Record<string, ScoringWeightConfig> => {
  const newWeights = { ...currentWeights };

  if (enabled) {
    // When enabling, set to a default value
    const currentValue = currentWeights[component].value || 0.2; // Default to 20%
    newWeights[component] = {
      ...currentWeights[component],
      value: currentValue
    };

    // Only renormalize if normalizeMode is enabled
    if (normalizeMode) {
      const enabledFactors = Object.entries(newWeights).filter(([_, config]) => config.value > 0);
      const totalSum = enabledFactors.reduce((acc, [_, config]) => acc + config.value, 0);

      if (totalSum > 0) {
        enabledFactors.forEach(([key, config]) => {
          newWeights[key] = {
            ...config,
            value: config.value / totalSum
          };
        });
      }
    }
  } else {
    // When disabling, set to 0
    newWeights[component] = {
      ...currentWeights[component],
      value: 0
    };

    // Only renormalize if normalizeMode is enabled
    if (normalizeMode) {
      const enabledFactors = Object.entries(newWeights).filter(([_, config]) => config.value > 0);
      const totalSum = enabledFactors.reduce((acc, [_, config]) => acc + config.value, 0);

      if (totalSum > 0) {
        enabledFactors.forEach(([key, config]) => {
          newWeights[key] = {
            ...config,
            value: config.value / totalSum
          };
        });
      }
    }
  }

  return newWeights;
};

/**
 * ScoringTab Component
 * Handles the UI and interaction for the scoring factors configuration tab
 * 
 * @param scoringWeights - The current scoring weights configuration
 * @param onFactorChange - Callback when a slider value changes during drag
 * @param onFactorChangeCommitted - Callback when a slider drag is released
 * @param onFactorToggle - Callback when a factor is enabled/disabled
 */
const ScoringTab: React.FC<ScoringTabProps> = ({
  scoringWeights,
  onWeightsChange
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Internal state for UI preferences
  const [draggingSlider, setDraggingSlider] = React.useState<string | null>(null);
  const [normalizeMode, setNormalizeMode] = React.useState<boolean>(false);

  // Wrapper functions to manage dragging state and normalization internally
  const handleFactorChange = React.useCallback((component: string, newValue: number) => {
    setDraggingSlider(component);
    const normalizedWeights = normalizeScoringWeights(scoringWeights, component, newValue, normalizeMode);
    onWeightsChange(normalizedWeights);
  }, [scoringWeights, normalizeMode, onWeightsChange]);

  const handleFactorChangeCommitted = React.useCallback(() => {
    setDraggingSlider(null);
  }, []);

  const handleFactorToggle = React.useCallback((component: string, enabled: boolean) => {
    const normalizedWeights = toggleScoringFactor(scoringWeights, component, enabled, normalizeMode);
    onWeightsChange(normalizedWeights);
  }, [scoringWeights, normalizeMode, onWeightsChange]);

  /**
   * Normalize weight values to percentages that sum to 100%
   * When a slider is being dragged, we keep it at its exact value and normalize
   * only the other sliders proportionally. This prevents the dragged slider from
   * jumping to a different position due to normalization.
   * 
   * @param weights - Current scoring weights configuration
   * @param activeDragComponent - Component ID of the slider currently being dragged
   */
  const normalizeWeightsForDisplay = React.useCallback(
    (weights: Record<string, any>, activeDragComponent: string | null): Record<string, any> => {
      const normalized: Record<string, any> = {};
      
      // Get all enabled weights
      const enabledWeights = Object.entries(weights).filter(([_, w]) => w.value > 0);
      
      if (enabledWeights.length === 0) {
        // No enabled weights - return all as 0%
        Object.entries(weights).forEach(([key, weight]) => {
          normalized[key] = { ...weight, displayPercentage: 0 };
        });
        return normalized;
      }

      if (enabledWeights.length === 1) {
        // Only one enabled - it gets 100%
        const key = enabledWeights[0][0];
        Object.entries(weights).forEach(([k, w]) => {
          normalized[k] = {
            ...w,
            displayPercentage: k === key ? 100 : 0
          };
        });
        return normalized;
      }

      // If a slider is being dragged, keep it at its exact value and normalize others
      if (activeDragComponent && weights[activeDragComponent]?.value > 0) {
        const draggedValue = weights[activeDragComponent].value * 100;
        const remainingPercentage = 100 - draggedValue;
        
        // Sum of all other enabled weights (excluding dragged one)
        const otherWeightsSum = Object.entries(weights)
          .filter(([key, w]) => key !== activeDragComponent && w.value > 0)
          .reduce((acc, [_, w]) => acc + w.value, 0);
        
        Object.entries(weights).forEach(([key, weight]) => {
          if (key === activeDragComponent) {
            // Keep dragged slider at exact value
            normalized[key] = {
              ...weight,
              displayPercentage: draggedValue
            };
          } else if (weight.value > 0 && otherWeightsSum > 0) {
            // Normalize other sliders proportionally to fill remaining percentage
            normalized[key] = {
              ...weight,
              displayPercentage: (weight.value / otherWeightsSum) * remainingPercentage
            };
          } else {
            normalized[key] = {
              ...weight,
              displayPercentage: 0
            };
          }
        });
        
        return normalized;
      }

      // No slider being dragged - normalize all equally
      const totalSum = enabledWeights.reduce((acc, [_, w]) => acc + w.value, 0);
      
      Object.entries(weights).forEach(([key, weight]) => {
        normalized[key] = {
          ...weight,
          displayPercentage: weight.value > 0 ? (weight.value / totalSum) * 100 : 0
        };
      });

      return normalized;
    },
    []
  );

  // Compute normalized percentages for display only (doesn't modify form state)
  // Pass draggingSlider to keep the active slider at its exact value while normalizing others
  // When normalizeMode is off, just convert raw values to percentages without normalization
  const normalizedWeights = React.useMemo(
    () => {
      if (!normalizeMode) {
        // Free mode - show raw percentage values without normalization
        const weights: Record<string, any> = {};
        Object.entries(scoringWeights).forEach(([key, weight]) => {
          weights[key] = {
            ...weight,
            displayPercentage: weight.value * 100
          };
        });
        return weights;
      }
      return normalizeWeightsForDisplay(scoringWeights, draggingSlider);
    },
    [scoringWeights, draggingSlider, normalizeMode, normalizeWeightsForDisplay]
  );

  // Calculate total percentage for display
  const totalPercentage = React.useMemo(() => Object.values(normalizedWeights).reduce(
    (acc, config: any) => acc + (config.displayPercentage || 0),
    0
  ), [normalizedWeights]);

  return (
    <>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('Scoring Factors Configuration')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={normalizeMode}
                  onChange={(e) => setNormalizeMode(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {t('Auto-Normalize')}
                </Typography>
              }
            />
            <Chip
              label={`${t('Total')}: ${Math.round(totalPercentage)}%`}
              size="medium"
              sx={{
                color: '#FFFFFF',
                backgroundColor: normalizeMode 
                  ? (Math.abs(totalPercentage - 100) < 0.1 ? theme.palette.success.main : theme.palette.warning.main)
                  : theme.palette.text.darkGrey,
                fontWeight: 600,
                fontSize: '0.9rem',
                minWidth: 100
              }}
            />
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('Configure how offers are scored and ranked. Each factor represents a different aspect of an offer (price, distance, destination preference, etc.). The percentages show how much weight each factor has in calculating the final offer score. Higher scores indicate better offers that match your preferences.')}
        </Typography>
        <Alert severity={'info'} sx={{ mb: 2 }}>
          {normalizeMode 
            ? t('Auto-Normalize is ON: When you adjust one slider, the others will automatically adjust to maintain a total of 100%.')
            : t('Auto-Normalize is OFF: You can freely adjust each factor independently. Note: Weights will still be normalized to 100% during score calculation, but your raw values are preserved for reference.')}
        </Alert>
      </Box>

      {Object.keys(scoringWeights).length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('No Scoring Factors Configured')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('Scoring factors will be automatically loaded from the server when available.')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {Object.entries(scoringWeights)
            .sort(([, a], [, b]) => {
              // Sort enabled factors (value > 0) first, disabled factors (value = 0) last
              const aEnabled = a.value > 0;
              const bEnabled = b.value > 0;
              
              if (aEnabled && !bEnabled) return -1; // a enabled, b disabled -> a comes first
              if (!aEnabled && bEnabled) return 1;  // a disabled, b enabled -> b comes first
              return 0; // both same status, maintain original order
            })
            .map(([component, config]) => {
              const normalizedConfig = normalizedWeights[component] || config;
              return (
                <Box sx={{ minWidth: 480, maxWidth: 480, flex: '1' }} key={component}>
                  <ScoringFactorCard
                    key={component}
                    component={component}
                    config={config}
                    normalizedConfig={normalizedConfig as NormalizedScoringFactorConfig}
                    isDragging={draggingSlider === component}
                    onToggle={(enabled) => handleFactorToggle(component, enabled)}
                    onChange={(newValue: number) => handleFactorChange(component, newValue)}
                    onChangeCommitted={handleFactorChangeCommitted}
                  />
                </Box>
              );
            })}
        </Box>
      )}
    </>
  );
};

export default ScoringTab;
